import React from "react";
import { motion } from "framer-motion";
import { PhoneCall, FileText, Truck, Mic2 } from "lucide-react";

const steps = [
    {
        index: "01",
        icon:  PhoneCall,
        title: "Prvý kontakt",
        desc:  "Zavoláte nám alebo vyplníte dopytový formulár. Ešte ten deň vám odpovieme, prediskutujeme váš event a dohodneme sa na obhliadke miesta.",
        detail: "Bezplatná konzultácia",
        color: { accent: "#0099FF", faint: "rgba(0,153,255,0.07)", border: "rgba(0,153,255,0.25)" },
    },
    {
        index: "02",
        icon:  FileText,
        title: "Návrh a kalkulácia",
        desc:  "Pripravíme kompletný technický návrh s vizualizáciou pódia, výberom zariadení a detailnou cenovou ponukou — do 24 hodín.",
        detail: "Ponuka do 24 hodín",
        color: { accent: "#a78bfa", faint: "rgba(167,139,250,0.07)", border: "rgba(167,139,250,0.25)" },
    },
    {
        index: "03",
        icon:  Truck,
        title: "Dovoz a montáž",
        desc:  "V deň eventu dorazíme na miesto, postavíme truss a scénu, zapojíme všetky systémy a odladíme zvuk aj svetlo ešte pred príchodom hostí.",
        detail: "Skúsený tím na mieste",
        color: { accent: "#34d399", faint: "rgba(52,211,153,0.07)", border: "rgba(52,211,153,0.25)" },
    },
    {
        index: "04",
        icon:  Mic2,
        title: "Váš event žije",
        desc:  "Náš technik zostáva pri vás od začiatku až do konca. Vy sa venujete hosťom — my sa postaráme, aby všetko znelo a vyzeralo presne tak, ako ste si predstavili.",
        detail: "Podpora počas celého eventu",
        color: { accent: "#0099FF", faint: "rgba(0,153,255,0.07)", border: "rgba(0,153,255,0.25)" },
    },
];

function StepCard({ s, i }) {
    const Icon = s.icon;
    return (
        <motion.div
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.55, delay: i * 0.12 }}
            className="relative flex flex-col"
            data-testid={`process-step-${i}`}
        >
            {/* Big background number */}
            <div
                className="absolute -top-4 -left-1 font-heading font-black text-[7rem] leading-none select-none pointer-events-none"
                style={{ color: s.color.faint, WebkitTextStroke: `1px ${s.color.border}` }}
            >
                {s.index}
            </div>

            {/* Card */}
            <div
                className="relative flex-1 mt-10 rounded-2xl p-6 border bg-nova-surface/60 flex flex-col gap-4 transition-all duration-400 hover:-translate-y-1"
                style={{ borderColor: "rgba(255,255,255,0.07)" }}
                onMouseEnter={e => e.currentTarget.style.borderColor = s.color.border}
                onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"}
            >
                {/* Top accent bar */}
                <div className="absolute top-0 left-6 right-6 h-px rounded-full" style={{ background: `linear-gradient(90deg, transparent, ${s.color.accent}55, transparent)` }} />

                {/* Icon */}
                <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: s.color.faint, border: `1px solid ${s.color.border}` }}
                >
                    <Icon className="w-5 h-5" style={{ color: s.color.accent }} strokeWidth={1.6} />
                </div>

                {/* Title */}
                <h3 className="font-heading text-xl font-black text-white tracking-tight leading-tight">
                    {s.title}
                </h3>

                {/* Description */}
                <p className="text-zinc-400 text-sm leading-relaxed flex-1">
                    {s.desc}
                </p>

                {/* Detail pill */}
                <div
                    className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full text-xs font-medium"
                    style={{ background: s.color.faint, color: s.color.accent, border: `1px solid ${s.color.border}` }}
                >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color.accent }} />
                    {s.detail}
                </div>
            </div>
        </motion.div>
    );
}

export default function Process() {
    return (
        <section
            id="process"
            className="relative py-24 sm:py-32 px-6 sm:px-12 bg-nova-black overflow-hidden"
            data-testid="process-section"
        >
            {/* Subtle radial glow */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_60%,rgba(0,153,255,0.05),transparent)] pointer-events-none" />

            <div className="relative max-w-7xl mx-auto">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="max-w-2xl mb-20"
                >
                    <div className="font-mono text-xs uppercase tracking-[0.25em] text-nova-cyan mb-4">
                        / 05 — Ako to funguje
                    </div>
                    <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05]">
                        Od prvého hovoru{" "}
                        <span className="text-nova-cyan">po záverečný potlesk</span>
                    </h2>
                    <p className="mt-5 text-zinc-400 text-lg leading-relaxed">
                        Štyrmi krokmi k eventu, na ktorý sa nezabúda.
                    </p>
                </motion.div>

                {/* Steps grid */}
                <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-5">
                    {/* Connecting line — desktop only */}
                    <div className="hidden lg:block absolute top-[88px] left-[12.5%] right-[12.5%] h-px pointer-events-none z-0">
                        <div className="w-full h-full" style={{
                            background: "linear-gradient(90deg, rgba(0,153,255,0.3), rgba(167,139,250,0.3), rgba(52,211,153,0.3), rgba(0,153,255,0.3))"
                        }} />
                        {[0, 1, 2].map(n => (
                            <div
                                key={n}
                                className="absolute top-1/2 -translate-y-1/2 w-2 h-2 border-t-2 border-r-2 rotate-45"
                                style={{ left: `calc(${33.3 * (n + 1)}% - 4px)`, borderColor: "rgba(255,255,255,0.15)" }}
                            />
                        ))}
                    </div>

                    {steps.map((s, i) => (
                        <StepCard key={s.index} s={s} i={i} />
                    ))}
                </div>

                {/* Bottom stats strip */}
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                    className="mt-16 pt-8 border-t border-white/5 grid grid-cols-2 sm:grid-cols-4 gap-6"
                >
                    {[
                        { val: "do 24h",  label: "Odpoveď na dopyt" },
                        { val: "zadarmo", label: "Obhliadka miesta" },
                        { val: "100%",    label: "Technik na mieste" },
                        { val: "10+",     label: "Rokov skúseností" },
                    ].map((s, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: 0.6 + i * 0.08 }}
                            className="flex flex-col gap-1"
                        >
                            <div className="font-heading text-2xl font-black text-white">{s.val}</div>
                            <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{s.label}</div>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
}
